import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { getCartManager } from '@theme/cart-manager';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} CartSubscriptionRefs
 * @property {HTMLElement} [subscribeRow]
 * @property {HTMLElement} [noSubscribeRow]
 * @property {HTMLInputElement} [subscribeRadio]
 * @property {HTMLInputElement} [noSubscribeRadio]
 * @property {HTMLDetailsElement} [planSelect]
 * @property {HTMLElement} [planLabel]
 */

/**
 * Inline Withings+ subscription selector rendered under an eligible device line
 * in the cart drawer / cart page (see snippets/cart-subscription.liquid).
 *
 * Unlike the PDP block (a bundle-store contributor), this performs REAL cart
 * mutations. The subscription is a SEPARATE product; the monthly/annual choice is
 * a VARIANT of it (no Shopify selling plan). The subscription line is a NESTED
 * cart line under its device (`parent_line_key`), marked by the
 * `_subscription_term` / `_trial_period` properties and linked to its device by
 * a shared `_sales_line_item_group_id`. One subscription per device line:
 *  - **Subscribe**: stamp the group id on the device line (no morph, re-keys
 *    it), then add the chosen variant nested under the fresh device key
 *    (single morph).
 *  - **Change variant**: remove the old subscription line, add the new variant
 *    nested under the same device (same group id).
 *  - **Unsubscribe**: set the subscription line quantity to 0.
 *
 * State is derived server-side from the cart on every render; this component
 * re-reads its dataset on connect AND on morph (`updatedCallback`).
 *
 * A module-level reconciler removes an orphan subscription line when its device
 * is gone — Shopify cascades nested lines natively, so it only matters for
 * legacy flat subscription lines added before the nested migration.
 *
 * @extends Component<CartSubscriptionRefs>
 */
class CartSubscription extends Component {
  /** @type {string} */
  #deviceKey = '';
  /** @type {string} */
  #groupId = '';
  /** @type {'subscribed' | 'none'} */
  #state = 'none';
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {boolean} - Guards against overlapping mutations. */
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#readState();
  }

  updatedCallback() {
    super.updatedCallback();
    // The morph recycles this node — re-read the dataset the server re-rendered.
    this.#readState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /** Cache the server-rendered state from the dataset. */
  #readState() {
    this.#deviceKey = this.dataset.deviceKey ?? '';
    this.#groupId = this.dataset.groupId ?? '';
    this.#state = this.dataset.state === 'subscribed' ? 'subscribed' : 'none';
  }

  /** @returns {any} The live cart-items-component, or null. */
  #cartItems() {
    const el = this.closest('cart-items-component');
    return el && typeof (/** @type {any} */ (el).processCartOperation) === 'function'
      ? /** @type {any} */ (el)
      : null;
  }

  /**
   * Public — radio toggle between "Abonnement" and "Sans abonnement".
   * @param {Event} event
   */
  handleToggle(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || this.#busy) return;
    // Re-read the server-rendered state — the morph may have replaced/recycled
    // this node since connect, leaving the cached state stale.
    this.#readState();

    const subscribe = this.querySelector('[ref="subscribeRadio"]');
    const noSubscribe = this.querySelector('[ref="noSubscribeRadio"]');

    if (target.value === 'subscribe' && target.checked) {
      if (noSubscribe instanceof HTMLInputElement) noSubscribe.checked = false;
      this.#syncOptionStyles(true);
      if (this.#state !== 'subscribed') {
        this.#addSubscription(this.#selectedVariantId(), this.#selectedPlanData());
      }
    } else if (target.value === 'none' && target.checked) {
      if (subscribe instanceof HTMLInputElement) subscribe.checked = false;
      this.#syncOptionStyles(false);
      if (this.#state === 'subscribed') this.#removeSubscription();
    }
  }

  /**
   * Public — Enter on a focused radio checks it (native radios only check on Space).
   * @param {KeyboardEvent} event
   */
  handleRadioKeydown(event) {
    if (event.key !== 'Enter') return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    event.preventDefault();
    if (target.checked) return;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Public — picks a variant from the dropdown. Subscribes (or swaps variant).
   * @param {string} variantId - The chosen variant id (option `data-plan-id`).
   */
  handlePlanSelect(variantId) {
    if (this.#busy) return;
    // Re-read the server-rendered state — the morph may have replaced/recycled
    // this node since connect, leaving the cached state stale.
    this.#readState();
    const button = this.querySelector(`.subscription-block__plan-option[data-plan-id="${variantId}"]`);
    if (!(button instanceof HTMLElement)) return;

    this.#setSelectedOption(button);
    const dropdown = this.querySelector('[ref="planSelect"]');
    if (dropdown instanceof HTMLDetailsElement) dropdown.removeAttribute('open');

    const plan = {
      term: button.dataset.planTerm ?? '',
      trialPeriod: button.dataset.planTrialPeriod ?? '',
      sellingPlanId: button.dataset.planSellingPlanId ?? '',
    };

    if (this.#state === 'subscribed') {
      this.#changeVariant(variantId, plan);
      return;
    }
    // Picking a variant implies "subscribe".
    const subscribe = this.querySelector('[ref="subscribeRadio"]');
    const noSubscribe = this.querySelector('[ref="noSubscribeRadio"]');
    if (subscribe instanceof HTMLInputElement) subscribe.checked = true;
    if (noSubscribe instanceof HTMLInputElement) noSubscribe.checked = false;
    this.#syncOptionStyles(true);
    this.#addSubscription(variantId, plan);
  }

  /**
   * Subscribe: stamp the device with a fresh group id (no morph, re-keys the
   * device line) then add the chosen variant NESTED under the fresh device key
   * (single morph).
   * @param {string} variantId
   * @param {{ term: string, trialPeriod: string, sellingPlanId: string }} plan
   */
  async #addSubscription(variantId, plan) {
    const cartItems = this.#cartItems();
    if (!cartItems || !variantId) return;

    // Abort (with UI revert) when the device line can't be resolved — adding a
    // flat subscription line without a stamped device would only produce an
    // orphan the reconciler removes on the next cart:update (silent failure).
    const deviceLine = findLine(getCartManager().cartJSON, this.#deviceKey);
    if (!deviceLine) {
      this.#revert();
      return;
    }

    this.#busy = true;
    // Block the footer CTAs for the WHOLE flow — step 1 is a raw fetch that
    // bypasses processCartOperation's own disable, so cover it here.
    this.#setCheckoutBusy(true);
    this.#setGroupBusy(true);
    this.#abort?.abort();
    this.#abort = new AbortController();
    const groupId = generateGroupId();

    try {
      // 1. Stamp the shared group id on the existing device line (no morph).
      //    /cart/change.js REPLACES the line's properties and re-keys it, so we
      //    merge the existing ones and read the fresh key from the response —
      //    it becomes the nesting anchor. `sections` keeps the cart-manager quiet.
      const response = await fetch(cartChangeJsUrl(), {
        ...fetchConfig('json', {
          body: JSON.stringify({
            id: this.#deviceKey,
            quantity: deviceLine.quantity,
            properties: { ...(deviceLine.properties ?? {}), _sales_line_item_group_id: groupId },
            sections: sectionIds(),
            sections_url: window.location.pathname,
          }),
        }),
        signal: this.#abort.signal,
      });
      if (!response.ok) {
        throw new Error(`CartSubscription: group id stamp failed (HTTP ${response.status})`);
      }
      const changed = await response.json();
      const parentKey = findDeviceLineForGroup(changed, groupId)?.key ?? '';
      if (!parentKey) {
        throw new Error('CartSubscription: fresh device key not found after stamp');
      }

      // 2. Add the Withings+ variant line nested under the fresh device key,
      //    with its selling plan (single morph + event).
      /** @type {Record<string, string>} */
      const properties = { _sales_line_item_group_id: groupId, _subscription_term: plan.term };
      if (plan.trialPeriod) properties._trial_period = plan.trialPeriod;
      /** @type {Record<string, unknown>} */
      const item = { id: Number(variantId), quantity: 1, properties, parent_line_key: parentKey };
      if (plan.sellingPlanId) item.selling_plan = Number(plan.sellingPlanId);

      await cartItems.processCartOperation({
        operation: 'add',
        data: { items: [item] },
      });
    } catch (error) {
      if (/** @type {any} */ (error)?.name !== 'AbortError') {
        console.error('CartSubscription: add failed', error);
        this.#revert();
      }
    } finally {
      this.#busy = false;
      this.#setCheckoutBusy(false);
      this.#setGroupBusy(false);
    }
  }

  /**
   * Swap the subscription to a different variant (different price/term) while
   * keeping the same device link. A variant can't be changed in place via
   * /cart/change.js (and a nested relation is immutable), so we remove the old
   * line (no morph) then add the new one nested under the same device.
   * @param {string} variantId
   * @param {{ term: string, trialPeriod: string, sellingPlanId: string }} plan
   */
  async #changeVariant(variantId, plan) {
    const cartItems = this.#cartItems();
    if (!cartItems || !variantId) return;
    const cart = getCartManager().cartJSON;
    const subLine = findSubLineForGroup(cart, this.#groupId);
    if (!subLine) return;
    // No-op if the same variant is already selected.
    if (Number(subLine.variant_id) === Number(variantId)) return;

    this.#busy = true;
    this.#setCheckoutBusy(true);
    this.#setGroupBusy(true);
    this.#abort?.abort();
    this.#abort = new AbortController();

    try {
      // 1. Remove the old subscription line (no morph; cart-manager stays quiet).
      //    A 404 means the line is already gone (another tab) — safe to proceed.
      //    Any other failure must abort: adding without removing would leave TWO
      //    subscription lines nested under the same device.
      const response = await fetch(cartChangeJsUrl(), {
        ...fetchConfig('json', {
          body: JSON.stringify({
            id: subLine.key,
            quantity: 0,
            sections: sectionIds(),
            sections_url: window.location.pathname,
          }),
        }),
        signal: this.#abort.signal,
      });
      if (!response.ok && response.status !== 404) {
        throw new Error(`CartSubscription: old subscription line not removed (HTTP ${response.status})`);
      }
      if (response.ok) {
        const changed = await response.json().catch(() => null);
        const stillThere = changed?.items?.some?.((/** @type {any} */ it) => it.key === subLine.key);
        if (stillThere) {
          throw new Error('CartSubscription: old subscription line still in cart after removal');
        }
      }

      // 2. Add the new variant line, same group id, nested under the device,
      //    with its selling plan (single morph + event). The device key is
      //    unchanged by step 1.
      /** @type {Record<string, string>} */
      const properties = { _sales_line_item_group_id: this.#groupId, _subscription_term: plan.term };
      if (plan.trialPeriod) properties._trial_period = plan.trialPeriod;
      /** @type {Record<string, unknown>} */
      const item = { id: Number(variantId), quantity: 1, properties };
      if (this.#deviceKey) item.parent_line_key = this.#deviceKey;
      if (plan.sellingPlanId) item.selling_plan = Number(plan.sellingPlanId);

      await cartItems.processCartOperation({
        operation: 'add',
        data: { items: [item] },
      });
    } catch (error) {
      if (/** @type {any} */ (error)?.name !== 'AbortError') {
        console.error('CartSubscription: change variant failed', error);
        this.#revert();
      }
    } finally {
      this.#busy = false;
      this.#setCheckoutBusy(false);
      this.#setGroupBusy(false);
    }
  }

  /** Unsubscribe: remove the subscription line (quantity 0). */
  async #removeSubscription() {
    const cartItems = this.#cartItems();
    if (!cartItems) return;
    const subLine = findSubLineForGroup(getCartManager().cartJSON, this.#groupId);
    if (!subLine) {
      this.#revert();
      return;
    }

    this.#busy = true;
    this.#setGroupBusy(true);
    try {
      await cartItems.processCartOperation({
        operation: 'change',
        data: { id: subLine.key, quantity: 0 },
        options: { keys: subLine.key },
      });
    } catch (error) {
      console.error('CartSubscription: remove failed', error);
    } finally {
      this.#busy = false;
      this.#setGroupBusy(false);
    }
  }

  /**
   * Toggle the cart "busy" state on the live cart-items-component. Reuses the
   * theme's `cart-items-disabled` class (pointer-events: none on the whole
   * component) so the footer checkout/express buttons can't be clicked, and the
   * CSS dims them for visible feedback. The class lives on the component root,
   * which survives the morph (childrenOnly).
   * Resolve the component from the document (NOT `this.closest()`): the morph can
   * replace this element mid-operation, detaching it — `closest()` would then
   * return null and the disabled class would stay stuck, freezing the whole cart.
   * @param {boolean} busy
   */
  #setCheckoutBusy(busy) {
    const cic = document.querySelector('cart-items-component');
    cic?.classList.toggle('cart-items-disabled', busy);
  }

  /**
   * Toggle a loading state on the line items touched by a mutation: the owning
   * device line and this subscription selector. Gives the user visible feedback
   * (dimmed content + spinner) on the rows themselves, beyond the dimmed footer
   * CTAs. The device row is this selector row's previous sibling (the server
   * renders the selector immediately under its device). The morph strips the
   * class when it swaps in the fresh rows, so this naturally ends with the op.
   * @param {boolean} busy
   */
  #setGroupBusy(busy) {
    const subRow = this.closest('.cart-items__subscription-row');
    subRow?.previousElementSibling?.classList.toggle('is-subscription-loading', busy);
    this.classList.toggle('is-subscription-loading', busy);
  }

  /** Snap the radios/styles back to the current committed state (after a failed/blocked op). */
  #revert() {
    const subscribed = this.#state === 'subscribed';
    const subscribe = this.querySelector('[ref="subscribeRadio"]');
    const noSubscribe = this.querySelector('[ref="noSubscribeRadio"]');
    if (subscribe instanceof HTMLInputElement) subscribe.checked = subscribed;
    if (noSubscribe instanceof HTMLInputElement) noSubscribe.checked = !subscribed;
    this.#syncOptionStyles(subscribed);
  }

  /** @returns {string} The currently aria-selected variant id (fallback: first option). */
  #selectedVariantId() {
    const active = this.querySelector('.subscription-block__plan-option[aria-selected="true"]');
    if (active instanceof HTMLElement && active.dataset.planId) return active.dataset.planId;
    const first = this.querySelector('.subscription-block__plan-option');
    return first instanceof HTMLElement ? (first.dataset.planId ?? '') : '';
  }

  /** @returns {{ term: string, trialPeriod: string, sellingPlanId: string }} Plan data from the aria-selected option (fallback: first option). */
  #selectedPlanData() {
    const active =
      this.querySelector('.subscription-block__plan-option[aria-selected="true"]') ??
      this.querySelector('.subscription-block__plan-option');
    const dataset = active instanceof HTMLElement ? active.dataset : {};
    return {
      term: dataset.planTerm ?? '',
      trialPeriod: dataset.planTrialPeriod ?? '',
      sellingPlanId: dataset.planSellingPlanId ?? '',
    };
  }

  /** @param {HTMLElement} button */
  #setSelectedOption(button) {
    for (const opt of this.querySelectorAll('.subscription-block__plan-option')) {
      if (opt instanceof HTMLElement) opt.setAttribute('aria-selected', opt === button ? 'true' : 'false');
    }
    const label = this.querySelector('[ref="planLabel"]');
    if (label instanceof HTMLElement) label.textContent = button.dataset.planLabel ?? '';
  }

  /** @param {boolean} subscribed */
  #syncOptionStyles(subscribed) {
    const sub = this.querySelector('.subscription-block__option--subscribe');
    const none = this.querySelector('.subscription-block__option--no-subscribe');
    if (sub instanceof HTMLElement) sub.classList.toggle('is-selected', subscribed);
    if (none instanceof HTMLElement) none.classList.toggle('is-selected', !subscribed);
  }
}

/* ───────────────────────── helpers ───────────────────────── */

/** @returns {string} */
function generateGroupId() {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `wsub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Locale-aware `/cart/change.js` URL. `Theme.routes.cart_change_url` has no
 * `.js` suffix (unlike `cart_add_url`, see snippets/scripts.liquid) — the raw
 * fetches here need the JSON endpoint explicitly.
 * @returns {string}
 */
function cartChangeJsUrl() {
  const url = Theme.routes.cart_change_url ?? '/cart/change';
  return url.endsWith('.js') ? url : `${url}.js`;
}

/** @returns {string} Comma-separated cart section ids (keeps the cart-manager quiet). */
function sectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
}

/**
 * @param {any} cart
 * @param {string} key
 * @returns {any|null}
 */
function findLine(cart, key) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return items.find((it) => it.key === key) ?? null;
}

/**
 * The device line for a group = the line WITHOUT `_subscription_term` carrying
 * the matching group id (used to resolve the fresh key after a re-keying stamp).
 * @param {any} cart
 * @param {string} groupId
 * @returns {any|null}
 */
function findDeviceLineForGroup(cart, groupId) {
  if (!groupId) return null;
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return (
    items.find(
      (it) => !it.properties?._subscription_term && it.properties?._sales_line_item_group_id === groupId
    ) ?? null
  );
}

/**
 * The subscription line for a group = the line carrying `_subscription_term`
 * with the matching group id (the device line shares the group id but has no term).
 * @param {any} cart
 * @param {string} groupId
 * @returns {any|null}
 */
function findSubLineForGroup(cart, groupId) {
  if (!groupId) return null;
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return (
    items.find(
      (it) => it.properties?._subscription_term && it.properties?._sales_line_item_group_id === groupId
    ) ?? null
  );
}

/* ───────── orphan-subscription reconciler (device-removal cascade) ─────────
 *
 * Shopify cascades NESTED subscription lines natively when their device is
 * removed. This single module-level listener remains as a safety net for legacy
 * FLAT subscription lines (added before the nested migration): it removes any
 * subscription line (marked by `_subscription_term`) whose group id no longer
 * matches a live device line — one orphan per pass, cascading via `cart:update`.
 * Idempotent: after removal there is no orphan, so it does not re-fire.
 * Skipped in the theme editor.
 */
let reconcilerInstalled = false;

function reconcileOrphanSubscriptions() {
  if (window.Shopify?.designMode) return;
  const cart = getCartManager().cartJSON;
  const items = Array.isArray(cart?.items) ? cart.items : [];

  // Device lines carry the group id but NOT the subscription-term marker.
  const deviceGroupIds = new Set(
    items
      .filter((it) => !it.properties?._subscription_term && it.properties?._sales_line_item_group_id)
      .map((it) => it.properties._sales_line_item_group_id)
  );

  const orphan = items.find(
    (it) =>
      it.properties?._subscription_term &&
      it.properties?._sales_line_item_group_id &&
      !deviceGroupIds.has(it.properties._sales_line_item_group_id)
  );
  if (!orphan) return;

  const cartItems = document.querySelector('cart-items-component');
  if (!cartItems || typeof (/** @type {any} */ (cartItems).processCartOperation) !== 'function') return;

  /** @type {any} */ (cartItems)
    .processCartOperation({ operation: 'change', data: { id: orphan.key, quantity: 0 }, options: { keys: orphan.key } })
    .catch(() => {});
}

function installReconciler() {
  if (reconcilerInstalled) return;
  reconcilerInstalled = true;
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    // Don't double-act on echoes broadcast from another tab.
    if (/** @type {any} */ (event)?.detail?.data?.source === 'cart-broadcast') return;
    reconcileOrphanSubscriptions();
  });
}

installReconciler();

if (!customElements.get('cart-subscription')) {
  customElements.define('cart-subscription', CartSubscription);
}
